import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { clientAPI } from '../services/api';
import Layout from '../components/Layout';
import PhoneInput, { isValidPhoneNumber, getCountries, getCountryCallingCode, parsePhoneNumber } from 'react-phone-number-input';
import Input from 'react-phone-number-input/input';
import DocumentUpload from '../components/DocumentUpload';
import en from 'react-phone-number-input/locale/en';
import 'react-phone-number-input/style.css';
import { toast } from 'sonner';
import './AddClient.css';
import { COUNTRY_CURRENCY_MAP, CURRENCY_LABELS } from '../utils/currencyMap';

const Icon = ({ name, size = 20 }) => {
  const icons = {
    arrowLeft: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>,
    save: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg>,
  };
  return icons[name] || null;
};

const EditClient = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contactPerson: '',
    email: '',
    phone: '',
    mobileCountry: '',
    contractType: '',
    billingFrequency: '',
    contractValue: '',
    industry: '',
    location: '',
    address: '',
    gstVatEin: '',
    technologyStack: '',
    isActive: true,
    currency: 'INR'
  });
  const [selectedMobileCountry, setSelectedMobileCountry] = useState('IN');
  const [phoneError, setPhoneError] = useState('');



  useEffect(() => {
    loadClient();
  }, [id]);

  const loadClient = async () => {
    try {
      const response = await clientAPI.getById(id);
      const client = response.data.data;
      setFormData({
        name: client.name || '',
        contactPerson: client.contactPerson || '',
        email: client.email || '',
        phone: client.phone || '',
        contractType: client.contractType || '',
        billingFrequency: client.billingFrequency || '',
        contractValue: client.contractValue || '',
        currency: client.currency || 'INR',
        industry: client.industry || '',
        location: client.location || '',
        address: client.address || '',
        gstVatEin: client.gstVatEin || '',
        technologyStack: client.technologyStack ? client.technologyStack.join(', ') : '',
        isActive: client.isActive,
        mobileCountry: client.mobileCountry || ''
      });

      // Auto-detect country from phone number if mobileCountry is not stored
      if (client.mobileCountry) {
        setSelectedMobileCountry(client.mobileCountry);
      } else if (client.phone) {
        const parsed = parsePhoneNumber(client.phone);
        if (parsed && parsed.country) {
          setSelectedMobileCountry(parsed.country);
        }
      }
    } catch (error) {
      alert('Failed to load client');
      navigate('/clients');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        contractValue: parseFloat(formData.contractValue) || 0,
        technologyStack: formData.technologyStack ? formData.technologyStack.split(',').map(s => s.trim()) : [],
        communicationEmail: formData.email
      };

      await clientAPI.update(id, submitData);
      toast.success('Client updated successfully!');
      navigate('/clients');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update client');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="add-client-page">
        <div className="page-header">
          <button className="btn-back" onClick={() => navigate('/clients')}>
            <Icon name="arrowLeft" size={20} />
            <span>Back to Clients</span>
          </button>
          <h1 className="page-title">Edit Client</h1>
          <p className="page-subtitle">Update client information and contract details</p>
        </div>

        <div className="form-container">
          <form onSubmit={handleSubmit} className="client-form">
            <div className="form-section">
              <h2 className="section-title">Basic Information</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label>Client Name <span className="required">*</span></label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter client name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Industry</label>
                  <input
                    type="text"
                    name="industry"
                    value={formData.industry}
                    onChange={handleInputChange}
                    placeholder="e.g., Technology, Healthcare"
                  />
                </div>

                <div className="form-group">
                  <label>Contact Person <span className="required">*</span></label>
                  <input
                    type="text"
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={handleInputChange}
                    placeholder="Enter contact person name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email <span className="required">*</span></label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="contact@company.com"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Phone</label>
                  <div className={`premium-phone-input ${phoneError ? 'has-error' : ''}`}>
                    <div className="country-select-wrapper">
                      {selectedMobileCountry ? (
                        <>
                          <img
                            src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${selectedMobileCountry}.svg`}
                            alt={selectedMobileCountry}
                            className="flag-icon"
                          />
                          <span className="calling-code">+{getCountryCallingCode(selectedMobileCountry)}</span>
                        </>
                      ) : (
                        <div className="globe-icon">🌐</div>
                      )}
                      <select
                        className="country-native-select"
                        value={selectedMobileCountry || ''}
                        onChange={(e) => {
                          const newCountry = e.target.value || null;
                          setSelectedMobileCountry(newCountry);
                          setFormData(prev => ({
                            ...prev,
                            mobileCountry: newCountry || '',
                            currency: COUNTRY_CURRENCY_MAP[newCountry] || prev.currency
                          }));
                          setPhoneError('');
                        }}
                        disabled={loading}
                      >
                        <option value="">Select Country</option>
                        {getCountries().map((country) => (
                          <option key={country} value={country}>
                            {en[country]} (+{getCountryCallingCode(country)})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="phone-input-field-wrapper">
                      <Input
                        country={selectedMobileCountry}
                        placeholder={selectedMobileCountry ? `Enter number for ${en[selectedMobileCountry]}` : "Select country first"}
                        value={formData.phone}
                        onChange={(value) => {
                          setFormData(prev => ({ ...prev, phone: value || '' }));

                          // Real-time validation
                          if (value) {
                            if (isValidPhoneNumber(value)) {
                              setPhoneError('');
                            } else {
                              setPhoneError('Please enter a valid phone number for the selected country');
                            }
                          } else {
                            setPhoneError('');
                          }
                        }}
                        disabled={!selectedMobileCountry || loading}
                        className="PhoneInputInput"
                      />
                    </div>
                  </div>
                  {phoneError && <span className="error-text" style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{phoneError}</span>}
                </div>

                <div className="form-group">
                  <label>Location</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="City, State/Country"
                  />
                </div>
              </div>

              <div className="form-group full-width">
                <label>Address</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Enter full address"
                />
              </div>
            </div>

            <div className="form-section">
              <h2 className="section-title">Contract Details</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label>Contract Type <span className="required">*</span></label>
                  <select
                    name="contractType"
                    value={formData.contractType}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Type</option>
                    <option value="Fixed Price">Fixed Price</option>
                    <option value="Hourly Based">Hourly Based</option>
                    <option value="Resource-Based">Resource-Based</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Billing Frequency <span className="required">*</span></label>
                  <select
                    name="billingFrequency"
                    value={formData.billingFrequency}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Frequency</option>
                    <option value="Weekly">Weekly</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Milestone-based">Milestone-based</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Currency</label>
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleInputChange}
                    className="currency-select"
                  >
                    <option value="INR">INR (₹)</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="GBP">GBP (£)</option>
                    <option value="AUD">AUD (A$)</option>
                    <option value="CAD">CAD (C$)</option>
                    <option value="SGD">SGD (S$)</option>
                    <option value="AED">AED (د.إ)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Contract Value</label>
                  <div className="amount-input-wrapper">
                    <span className="currency-prefix">{formData.currency || 'INR'}</span>
                    <input
                      type="number"
                      name="contractValue"
                      value={formData.contractValue}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      min="0"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>GST/VAT/EIN</label>
                  <input
                    type="text"
                    name="gstVatEin"
                    value={formData.gstVatEin}
                    onChange={handleInputChange}
                    placeholder="Tax identification number"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  <span>Active Client</span>
                </label>
              </div>
            </div>

            <div className="form-section">
              <h2 className="section-title">Technical Information</h2>
              <div className="form-group full-width">
                <label>Technology Stack</label>
                <input
                  type="text"
                  name="technologyStack"
                  value={formData.technologyStack}
                  onChange={handleInputChange}
                  placeholder="e.g., React, Node.js, AWS (comma separated)"
                />
                <span className="form-hint">Enter technologies separated by commas</span>
              </div>
            </div>

            {/* Documents */}
            <div className="form-section">
              <h2 className="section-title">Documents</h2>
              <div className="form-group full-width">
                <label>Contracts & Agreements</label>
                <DocumentUpload
                  entityType="client"
                  entityId={id}
                  onUploadComplete={(docs) => console.log('Client docs uploaded', docs)}
                // existingDocuments={client?.documents || []}
                />
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => navigate('/clients')}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-submit"
                disabled={loading}
              >
                <Icon name="save" size={18} />
                <span>{loading ? 'Updating...' : 'Update Client'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default EditClient;
